import { motion } from "framer-motion";
import { useState } from "react";
import ModernButton from './ModernButton';

import {
  FiArrowUp,
  FiChevronLeft,
  FiChevronRight,
  FiLink,
  FiTarget,
  FiTool,
  FiShield
} from "react-icons/fi";

const CollapseCardFeatures = () => {
  const [position, setPosition] = useState(0);

  const shiftLeft = () => {
    if (position > 0) {
      setPosition((pv) => pv - 1);
    }
  };

  const shiftRight = () => {
    if (position < features.length - 1) {
      setPosition((pv) => pv + 1);
    }
  };

  return (
    <section className="overflow-hidden bg-gradient-to-br from-neutral-50 to-neutral-100 py-24 md:py-32">
      <div className="mx-auto w-full max-w-7xl px-5 md:px-10">
        <div className="mb-12 md:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between"
          >
            <div className="flex-1">
              <h2 className="text-4xl font-black leading-[1.2] text-neutral-900 md:text-6xl">
                Need something?{" "}
                <span className="block text-neutral-700 md:inline">
                  Here's what I can do:
                </span>
              </h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="mt-4 max-w-2xl text-lg text-neutral-600"
              >
                Explore my core services designed to elevate your digital presence
              </motion.p>
            </div>
            <div className="flex gap-3 md:self-start md:mt-1">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="h-fit rounded-lg bg-neutral-900 p-4 text-2xl text-white shadow-lg transition-all hover:bg-neutral-800 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={shiftLeft}
                disabled={position === 0}
              >
                <FiChevronLeft />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="h-fit rounded-lg bg-neutral-900 p-4 text-2xl text-white shadow-lg transition-all hover:bg-neutral-800 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={shiftRight}
                disabled={position === features.length - 1}
              >
                <FiChevronRight />
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="relative mb-8 overflow-visible w-full">
        <div className="mx-auto w-full max-w-7xl">
          <motion.div
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={(_, { offset, velocity }) => {
              const swipe = Math.abs(offset.x) * velocity.x;
              if (swipe < -10000 && position < features.length - 1) {
                shiftRight();
              } else if (swipe > 10000 && position > 0) {
                shiftLeft();
              }
            }}
            className="flex justify-start gap-6 cursor-grab active:cursor-grabbing pl-5 md:pl-10"
          >
            {features.map((feat, index) => (
              <Feature {...feat} key={index} position={position} index={index} />
            ))}
          </motion.div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-12 flex justify-center"
        >
          <ModernButton text="See my services!" link="/services" />
        </motion.div>
      </div>
    </section>
  );
};

const Feature = ({ position, index, title, description, Icon }) => {
  // Calculate horizontal translation for sliding effect - same as original
  const translateAmt = position >= index
    ? index * 100
    : index * 100 - 100 * (index - position);

  // Fixed z-index: card 0 = 9991, card 1 = 9992, card 2 = 9993, etc.
  const zIndex = 9991 + index;

  return (
    <motion.div
      animate={{ x: `${-translateAmt}%` }}
      transition={{
        ease: "easeInOut",
        duration: 0.5,
      }}
      style={{ zIndex }}
      className={`relative flex min-h-[360px] w-10/12 max-w-lg shrink-0 flex-col justify-start overflow-hidden rounded-2xl p-8 shadow-2xl md:w-3/5 ${
        index % 2
          ? "bg-gradient-to-br from-neutral-900 to-neutral-800 text-white"
          : "bg-white border-2 border-neutral-200"
      }`}
    >
      {/* Decorative background element */}
      <div
        className={`absolute -right-8 -top-8 h-32 w-32 rounded-full ${
          index % 2 ? "bg-indigo-600/10" : "bg-indigo-600/5"
        } blur-3xl`}
      />

      {/* Icon */}
      <div className="relative z-10">
        <div
          className={`mb-6 inline-flex rounded-xl p-4 ${
            index % 2
              ? "bg-indigo-600/20 text-indigo-400"
              : "bg-indigo-600/10 text-indigo-600"
          }`}
        >
          <Icon className="text-4xl" />
        </div>
        <h3
          className={`mb-4 text-3xl font-black ${
            index % 2 ? "text-white" : "text-neutral-900"
          }`}
        >
          {title}
        </h3>
      </div>

      <p
        className={`relative z-10 text-base leading-relaxed ${
          index % 2 ? "text-neutral-300" : "text-neutral-700"
        }`}
      >
        {description}
      </p>

      {/* Card number indicator */}
      <div className="absolute bottom-4 right-6 text-6xl font-black opacity-5">
        {String(index + 1).padStart(2, "0")}
      </div>
    </motion.div>
  );
};

export default CollapseCardFeatures;

const features = [
  {
    title: "Custom Web Development",
    Icon: FiTool,
    description:
      "Tailor-made websites and applications built to meet unique business needs, with a focus on WordPress, PHP, and modern JavaScript frameworks.",
  },
  {
    title: "SEO Optimization",
    Icon: FiTarget,
    description:
      "Enhance visibility and reach with in-depth SEO strategies, including on-page optimization, keyword targeting, and performance improvements.",
  },
  {
    title: "Data-Driven Insights",
    Icon: FiArrowUp,
    description:
      "Unlock actionable insights through advanced analytics, including server-side tagging, to make smarter, data-driven decisions that drive growth.",
  },
  {
    title: "GDPR & Cookie Compliance",
    Icon: FiShield,
    description:
      "Ensure compliance with GDPR and other data privacy regulations, including Cookiebot integration and best practices for data handling.",
  },
  {
    title: "Efficient Integrations",
    Icon: FiLink,
    description:
      "Streamline workflows with seamless integration of 3rd-party tools, from CRM systems to marketing platforms, enabling smooth operation.",
  },
];

